import { Bar } from 'react-chartjs-2';
import React, { useMemo, useRef } from 'react';
import { Chart, Chart as ChartJS, TimeScale, TooltipModel } from 'chart.js';
import { useTranslation } from 'react-i18next';
import 'chartjs-adapter-luxon';
import Box from '@mui/material/Box';
import { GRAPH_DEFAULT_OPTIONS, chartBorderPlugin } from './graphConfig';
import { mapDataPointsToDatasets } from './utils/usageGraphUtils';
import { barRangePlugin, highlightPlugin } from './utils/graphPlugins';
import { useDispatchEventToChart } from './common/Graphs/hooks/useDispatchEventToChart';
import { EmptyLineOrBarChart } from './common/Graphs/EmptyLineOrBarChart';

const HEIGHT = 185;

export type UsageChartEntryData = {
  x: [Date, Date];
  inputUsage: number;
  outputUsage: number;
  tooltipData: {
    label: string;
    dataIndex: number;
  };
};

export type SingleUsageGraphProps = {
  shouldDisplayPlaceholderChart?: boolean;
  entries: UsageChartEntryData[];
  from: number;
  to: number;
  highlightFrom?: Date | null;
  highlightTo?: Date | null;
  onZoomComplete: (range: [Date, Date]) => void;
  tooltip: (positionX: number, index: number | null, setName: string | null) => void;
  onClick: () => void;
  onPan?: (newMin: Date, newMax: Date) => void;
  min: number;
  max: number;
};

export const SingleUsageGraph: React.FC<SingleUsageGraphProps> = React.memo(
  ({
    shouldDisplayPlaceholderChart,
    entries,
    highlightFrom,
    highlightTo,
    from,
    to,
    min,
    max,
    onZoomComplete,
    tooltip,
    onClick,
    onPan,
  }) => {
    const { t } = useTranslation();

    const { datasets, maxValue } = useMemo(() => {
      const { data, maxValue } = mapDataPointsToDatasets(entries);

      return {
        datasets: {
          datasets: [
            {
              barThickness: 1,
              data,
            },
          ],
        },
        maxValue,
      };
    }, [entries]);

    // @ts-expect-error -- resolve options
    const options: Chart<'bar'>['options'] = useMemo(
      () =>
        ({
          ...GRAPH_DEFAULT_OPTIONS,
          indexAxis: 'y',
          scales: {
            x: {
              ...GRAPH_DEFAULT_OPTIONS!.scales!.x,
              type: 'time',
              min: new Date(from).getTime(),
              max: new Date(to).getTime(),
            },
            y: {
              ...GRAPH_DEFAULT_OPTIONS!.scales!.y,
              type: 'linear',
              min: 0,
              max: 1.2 * maxValue,
              display: false,
              beginAtZero: true,
              offset: false,
              grid: {
                ...GRAPH_DEFAULT_OPTIONS!.scales!.y?.grid,
                offset: false,
              },
            },
          },
          plugins: {
            ...GRAPH_DEFAULT_OPTIONS!.plugins,
            zoom: {
              ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom,
              limits: {
                ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.limits,
                x: {
                  ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.limits?.x,
                  min: min,
                  max: max,
                },
              },
              zoom: {
                ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.zoom,
                enabled: false,
                onZoomComplete(context) {
                  onZoomComplete([
                    new Date(context.chart.scales.x.min),
                    new Date(context.chart.scales.x.max),
                  ]);
                },
              },
              pan: {
                ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.pan,
                mode: 'x',
                scaleMode: 'x',

                threshold: 200,
                onPan: (event) => {
                  if (onPan) {
                    // @ts-expect-error -- min doesn't exist
                    const min = new Date(event.chart.boxes[0].min);
                    // @ts-expect-error -- max doesn't exist
                    const max = new Date(event.chart.boxes[0].max);
                    onPan(min, max);
                  }
                },
              },
            },
            tooltip: {
              enabled: false,
              axis: 'x',
              mode: 'x',
              intersect: false,
              external: (context: { tooltip: TooltipModel<'bar'>; chart: ChartJS<'bar'> }) => {
                if (context.tooltip.opacity === 0) {
                  tooltip(0, null, null);
                  return;
                }
                const dataPoint = context.tooltip.dataPoints[0];
                const pointRaw = dataPoint.raw;
                // @ts-expect-error -- type pointRaw
                const index = pointRaw.dataIndex;
                // @ts-expect-error -- type pointRaw
                const label = pointRaw.label;
                // @ts-expect-error -- type pointRaw
                const [startX, endX] = pointRaw.x;
                const scaleX = context.chart.scales.x as TimeScale;
                const startPositionX = scaleX.getPixelForValue(Math.max(startX, from));
                const endPositionX = scaleX.getPixelForValue(Math.min(endX, to));
                const tooltipPositionX = (startPositionX + endPositionX) / 2;
                tooltip(tooltipPositionX, index, label);
              },
            },
            highlightPlugin: {
              from: highlightFrom,
              to: highlightTo,
            },
          },
        } as const),
      [from, to, min, max, datasets, highlightFrom, highlightTo, tooltip, onZoomComplete],
    );

    const chartRef = useRef<ChartJS>(undefined);
    const onChartClick = () => {
      onClick();
    };

    useDispatchEventToChart(chartRef);

    if (shouldDisplayPlaceholderChart) {
      return (
        <Box sx={{ height: HEIGHT }}>
          <EmptyLineOrBarChart
            as={Bar}
            emptyChartText={t('rSimInventory.imsiMissing')}
            // @ts-expect-error -- fix types in the options
            xLabelOptions={options.scales.x}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ height: HEIGHT }}>
        <Bar
          plugins={[chartBorderPlugin, barRangePlugin, highlightPlugin]}
          data={datasets}
          options={options}
          // @ts-expect-error -- make ref type match with chart and data types
          ref={chartRef}
          onClick={onChartClick}
        />
      </Box>
    );
  },
);
